import { createContext, useContext, type ReactNode } from 'react'
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom'

import { ErrorCard } from './components/ErrorCard'
import { api, type MeResponse, type Project } from './lib/api'
import { useAsync } from './lib/useAsync'

interface ClientData {
  me: MeResponse
  projects: Project[]
}

const ClientContext = createContext<ClientData | null>(null)

/** Portal-wide client identity + project list. Throws outside the provider. */
export function useClient(): ClientData {
  const ctx = useContext(ClientContext)
  if (!ctx) throw new Error('useClient used outside ClientContext')
  return ctx
}

/** Extract :projectId from a /projects/:id/... pathname, or null. */
export function useCurrentProjectId(): string | null {
  const { pathname } = useLocation()
  const m = pathname.match(/\/projects\/([^/]+)/)
  return m ? m[1] : null
}

const PROJECT_TABS: ReadonlyArray<{ slug: string; label: string }> = [
  { slug: 'schedule', label: 'Schedule' },
  { slug: 'photos', label: 'Photos' },
  { slug: 'selections', label: 'Selections' },
  { slug: 'change-orders', label: 'Change Orders' },
  { slug: 'messages', label: 'Messages' },
  { slug: 'welcome-home', label: 'Welcome Home' },
]

function Nav(): JSX.Element {
  const projectId = useCurrentProjectId()
  return (
    <nav className="portal-nav" aria-label="Portal sections">
      <NavLink to="/" end className="nav-tab">
        Overview
      </NavLink>
      {projectId &&
        PROJECT_TABS.map((t) => (
          <NavLink
            key={t.slug}
            to={`/projects/${projectId}/${t.slug}`}
            className="nav-tab"
          >
            {t.label}
          </NavLink>
        ))}
    </nav>
  )
}

function Shell({
  clientName,
  children,
}: {
  clientName?: string
  children: ReactNode
}): JSX.Element {
  return (
    <div className="portal">
      <header className="portal-header">
        <Link to="/" className="brand">
          <img
            className="brand-logo"
            src="/logo.webp"
            alt="Heritage Homes"
            width={38}
            height={38}
          />
          <span className="brand-text">
            <span className="brand-mark">Heritage Homes</span>
            <span className="brand-sub">Client Portal</span>
          </span>
        </Link>
        <span className="header-client">
          {clientName ? `Signed in as ${clientName}` : ''}
        </span>
      </header>
      <Nav />
      <main className="portal-main">{children}</main>
      <footer className="portal-footer">
        <span>Heritage Homes LLC — your build, in one place.</span>
        <span className="build-tag">build {__BUILD_VERSION__}</span>
      </footer>
    </div>
  )
}

function LoadingShell(): JSX.Element {
  return (
    <Shell>
      <div className="skeleton-group" aria-busy="true" aria-label="Loading">
        <div className="skeleton-row" />
        <div className="skeleton-row" />
        <div className="skeleton-row" />
      </div>
    </Shell>
  )
}

/** Root layout — loads client identity + projects, then renders the shell. */
export function App(): JSX.Element {
  const state = useAsync<ClientData>(
    async () => {
      const [me, projects] = await Promise.all([api.me(), api.projects()])
      return { me, projects: projects.projects }
    },
    'client-root',
  )

  if (state.loading) return <LoadingShell />
  if (state.error || !state.data) {
    return (
      <Shell>
        <ErrorCard error={state.error} onRetry={state.reload} />
      </Shell>
    )
  }

  return (
    <ClientContext.Provider value={state.data}>
      <Shell clientName={state.data.me.display_name}>
        <Outlet />
      </Shell>
    </ClientContext.Provider>
  )
}
